import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ENUM_STORE_STATUS, ENUM_LCS_NAME } from "./enums";
import { API_CASINO } from "../api/casino";

const initialFavoritesState = {
  list: [],
  ids: [],
  status: ENUM_STORE_STATUS.idle,
  isFull: false,
  error: null
};

const historyState = {
  list: [],
  ids: JSON.parse(localStorage.getItem(ENUM_LCS_NAME.history) || "{}"),
  status: ENUM_STORE_STATUS.idle,
  isFull: false,
  error: null
};

export const fetchFavoriteGames = createAsyncThunk(
  "games/fetchFavoriteGames",
  async ({ countGames, currency }) => {
    const res = await API_CASINO.list.favorites({ maxGames: countGames, currency });
    return { list: res.data.list, countGames };
  },
  {
    condition: ({ countGames, currency }, { getState }) => {
      const { favorites } = getState().games;
      if (!currency) return false;
      if (favorites.status === "loading") return false;
      if (favorites.isFull) return false;
      if (countGames && favorites.list.length >= countGames) return false;
      return true;
    }
  }
);

export const fetchHistoryGames = createAsyncThunk(
  "games/fetchHistoryGames",
  async ({ countGames, currency }, { getState }) => {
    const { histories } = getState().games;
    const historyGamesId = histories?.ids[currency] || [];

    const gamesId = countGames
      ? historyGamesId.slice(0, countGames).join(",")
      : historyGamesId.join(",");

    if (!gamesId) return { list: [], countGames, currency };

    const res = await API_CASINO.list.games({ gameId: gamesId, currency });
    return { list: res.data.list, countGames, currency };
  },
  {
    condition: ({ countGames, currency }, { getState }) => {
      const { histories } = getState().games;
      const currencyState = histories;
      const historyGamesId = currencyState?.ids?.[currency] || [];

      if (!currency) return false;
      if (!historyGamesId.length) return false;
      if (currencyState?.status === ENUM_STORE_STATUS.loading) return false;
      if (currencyState?.isFull == currency) return false;
      if (countGames && (currencyState?.list.length || 0) >= countGames) return false;
      return true;
    }
  }
);

// - основная информация стора о играх
const gamesSlice = createSlice({
  name: "games",
  initialState: {
    favorites: initialFavoritesState,
    histories: historyState,
    categoryWager: {
      state: ENUM_STORE_STATUS.idle
    },
    excludeDemoMode: [],
    liveGames: [],
    categories: [],
    categoryById: {}, // [id]:{name:"",games:[]}
    providers: [],
    providersGamesById: {},
    hitGames: [],
    bonusProvider: null,
    bonusGames: [],
    idProvidersLiveGame: [], // список провайдеров лайв игр
    categoryList: [],
    categoryLoading: true,
    sportBookGame: null
  },
  reducers: {
    setProviders(state, newAction) {
      state.providers = newAction.payload;
    },
    setFavoriteGames(state, action) {
      const list = action.payload;
      state.favorites.list = list;
      state.favorites.ids = list.map((el) => el.game_id || el.id);
      localStorage.setItem(ENUM_LCS_NAME.favoritesId, JSON.stringify(state.favorites.ids));
    },
    addFavoriteGames(state, action) {
      const newGame = action.payload;
      const newGameId = newGame.game_id || newGame.id;
      if (!state.favorites.ids.includes(newGameId)) {
        state.favorites.list.push(newGame);
        state.favorites.ids.push(newGameId);
      }
      localStorage.setItem(ENUM_LCS_NAME.favoritesId, JSON.stringify(state.favorites.ids));
    },
    removeFavoriteGames(state, action) {
      const newGame = action.payload;
      const newGameId = newGame.game_id || newGame.id;
      state.favorites.list = state.favorites.list.filter(
        (el) => el.id != newGameId && el.game_id != newGameId
      );
      state.favorites.ids = state.favorites.ids.filter((id) => id != newGameId);
      localStorage.setItem(ENUM_LCS_NAME.favoritesId, JSON.stringify(state.favorites.ids));
    },

    setExcludeDemoMode(state, newAction) {
      state.excludeDemoMode = newAction.payload;
    },
    setLiveGames(state, newAction) {
      state.liveGames = newAction.payload;
    },
    setGameCategories(state, newAction) {
      state.categories = newAction.payload;
    },
    setFillCategoryById(state, newAction) {
      state.categoryById = newAction.payload;
    },
    setCategoryWager(state, newAction) {
      const wagerInfo = { ...newAction.payload, state: ENUM_STORE_STATUS.succeeded };
      state.categoryWager = wagerInfo;
    },
    setCategoryGamesById(state, newAction) {
      const { id, data } = newAction.payload;
      if (!state.categoryById[id]) state.categoryById[id] = { games: [] };
      state.categoryById[id].games = data;
    },
    setProvidersGamesById(state, newAction) {
      const { id, data } = newAction.payload;
      state.providersGamesById[id] = data;
    },
    setHistoryGames(state, newAction) {
      state.histories.list = newAction.payload;
    },
    setHitGames(state, newAction) {
      state.hitGames = newAction.payload;
    },
    setBonusProvider(state, newAction) {
      state.bonusProvider = newAction.payload;
    },
    setBonusGames(state, newAction) {
      state.bonusGames = newAction.payload;
    },
    setIdProvidersLiveGame(state, newAction) {
      state.idProvidersLiveGame = newAction.payload;
    },
    setCategoryList(state, newAction) {
      state.categoryList = newAction.payload;
    },
    setCategoryLoading(state, newAction) {
      state.categoryLoading = newAction.payload;
    },
    addHistoryIdList(state, newAction) {
      const { currency, gameId } = newAction.payload;
      const historyIdList = state.histories.ids?.[currency] || [];

      if (!historyIdList) state.histories.ids[currency] = [];
      state.histories.ids[currency] = historyIdList.filter((id) => id !== gameId);
      state.histories.ids[currency].push(gameId);
      state.histories.isFull = false;
      localStorage.setItem(ENUM_LCS_NAME.history, JSON.stringify(state.histories.ids));
    },
    removeHistoryId(state, newAction) {
      const { currency, gameId } = newAction.payload;
      const historyIdList = state.histories.ids?.[currency] || [];
      if (!historyIdList) state.histories.ids[currency] = [];
      state.histories.ids[currency] = historyIdList.filter((el) => el != gameId);
      state.histories.list = state.histories.list.filter((el) => el.game_id !== gameId);
      state.histories.isFull = false;
      localStorage.setItem(ENUM_LCS_NAME.history, JSON.stringify(state.histories.ids));
    },
    initHistoryIdList(state, newAction) {
      state.histories.ids = newAction.payload;
    },
    setSportBookGame(state, newAction) {
      state.sportBookGame = newAction.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFavoriteGames.pending, (state) => {
        state.favorites.status = "loading";
        state.favorites.error = null;
      })
      .addCase(fetchFavoriteGames.fulfilled, (state, action) => {
        const { list, countGames } = action.payload;
        state.favorites.list = list;
        state.favorites.ids = list.map((el) => el.game_id || el.id);
        state.favorites.status = "succeeded";
        if (!countGames) state.favorites.isFull = true;
        localStorage.setItem(ENUM_LCS_NAME.favoritesId, JSON.stringify(state.favorites.ids));
      })
      .addCase(fetchFavoriteGames.rejected, (state, action) => {
        state.favorites.status = "failed";
        state.favorites.error = action.error.message;
      })

      .addCase(fetchHistoryGames.pending, (state) => {
        state.histories.status = ENUM_STORE_STATUS.loading;
        state.histories.error = null;
      })
      .addCase(fetchHistoryGames.fulfilled, (state, action) => {
        const { list, countGames, currency } = action.payload;
        state.histories.list = list;
        state.histories.status = ENUM_STORE_STATUS.succeeded;
        if (!countGames) state.histories.isFull = currency;
      })
      .addCase(fetchHistoryGames.rejected, (state, action) => {
        state.histories.status = ENUM_STORE_STATUS.failed;
        state.histories.error = action.error.message;
      });
  }
});

export default gamesSlice.reducer;

export const {
  setProviders,
  setBonusGames,
  setGameCategories,
  setFavoriteGames,
  setExcludeDemoMode,
  addFavoriteGames,
  removeFavoriteGames,
  setLiveGames,
  setFillCategoryById,
  setCategoryWager,
  setCategoryGamesById,
  setProvidersGamesById,
  setHistoryGames,
  setBonusProvider,
  setIdProvidersLiveGame,
  setHitGames,
  setCategoryList,
  setCategoryLoading,
  addHistoryIdList,
  removeHistoryId,
  setSportBookGame,
  initHistoryIdList
} = gamesSlice.actions;
